import Big from "big.js";
import { models } from "types/api/viewModels.ts";
import { PriceType } from "types/declarations/enums.ts";

const manualPercentAdjustmentMap = {
	"+0%": 0,
	"+5%": 5,
	"+10%": 10,
	"+20%": 20,
	"+25%": 25,
	"+30%": 30,
	"+40%": 40,
	"+50%": 50
} as const;

export const getComplexityTierLabel = (percent: number | null | undefined): string | null => {
	if (percent === null || typeof percent === "undefined") {
		return null;
	}

	const foundTier = Object.entries(manualPercentAdjustmentMap).find(([, value]) => value === percent);
	return foundTier?.[0] ?? null;
};

export const sanitizePrefix = (percent: string | null | undefined): number => {
	if (!percent) {
		return 0;
	}

	const mappedPercent = manualPercentAdjustmentMap[percent as keyof typeof manualPercentAdjustmentMap];
	if (typeof mappedPercent !== "undefined") {
		return mappedPercent;
	}

	if (percent.includes("%")) {
		const beforePercent = percent.slice(0, percent.indexOf("%"));
		const percentTokens = beforePercent.match(/[+-]?\d+(?:\.\d+)?/g);
		const percentToken = percentTokens?.at(-1);
		if (percentToken) {
			return Number(percentToken);
		}
	}

	const numericValue = Number(percent.trim());
	return Number.isNaN(numericValue) ? 0 : numericValue;
};

export const sanitizeFlatAdjustment = (flatAdjustment: string | null | undefined): number => {
	if (!flatAdjustment) {
		return 0;
	}

	const normalizedValue = flatAdjustment.replace(/\$/g, "").replace(/,/g, "").trim();
	if (
		normalizedValue === "" ||
		normalizedValue === "+" ||
		normalizedValue === "-" ||
		normalizedValue === "." ||
		normalizedValue === "+." ||
		normalizedValue === "-."
	) {
		return 0;
	}

	const numericValue = Number(normalizedValue);
	return Number.isNaN(numericValue) ? 0 : numericValue;
};

export const calculateManualPercentAdjustmentAmount = (
	baseQuoteTotal: number,
	manualPercentAdjustment: string | null | undefined
): number => {
	const basePrice = new Big(baseQuoteTotal ?? 0);
	const manualPriceAdjPercent = sanitizePrefix(manualPercentAdjustment);
	return Number(basePrice.times(manualPriceAdjPercent).div(100).toFixed(2));
};

export const getComplexityTierPercent = (
	cqComplexityScoreTotal: number | null | undefined,
	complexityTierConfig?: models["CustomQuoteConfigViewModel"]
): number => {
	if (!cqComplexityScoreTotal || cqComplexityScoreTotal <= 0) {
		return 0;
	}

	if (!complexityTierConfig?.complexityTiers?.length) {
		return 0;
	}

	const sortedTiers = [...complexityTierConfig.complexityTiers].sort((a, b) => (a.maxScore ?? 0) - (b.maxScore ?? 0));

	for (const tier of sortedTiers) {
		if (cqComplexityScoreTotal <= (tier.maxScore ?? 0)) {
			return tier.chargePercent ?? 0;
		}
	}

	return complexityTierConfig.maxChargePercent ?? 0;
};

export const calculateCqComplexityAmount = (
	baseQuoteTotal: number,
	cqComplexityScoreTotal: number | null | undefined,
	complexityTierConfig?: models["CustomQuoteConfigViewModel"]
): number => {
	const basePrice = new Big(baseQuoteTotal ?? 0);
	const complexityTierPercent = getComplexityTierPercent(cqComplexityScoreTotal, complexityTierConfig);
	return Number(basePrice.times(complexityTierPercent).div(100).toFixed(2));
};

export const calculateSpecialModUpchargeAmount = (
	baseQuoteTotal: number | undefined,
	specialMods: models["SpecialModificationViewModel"][] | null
): number => {
	const modUpcharge =
		specialMods?.reduce((sum, mod) => {
			const price =
				mod?.priceType === PriceType.Fixed ? mod?.price : new Big(baseQuoteTotal ?? 0).mul(mod?.price ?? 0);
			return sum.plus(price ?? 0);
		}, new Big(0)) ?? new Big(0);

	return Number(modUpcharge.toFixed(2));
};

export const calculateCustomizationChargeAmount = (
	baseQuoteTotal: number,
	specialMods: models["SpecialModificationViewModel"][] | null,
	cqComplexityScoreTotal: number | null | undefined,
	complexityTierConfig?: models["CustomQuoteConfigViewModel"]
): number => {
	const specialModUpchargeAmount = new Big(calculateSpecialModUpchargeAmount(baseQuoteTotal, specialMods));
	const cqComplexityAmount = new Big(
		calculateCqComplexityAmount(baseQuoteTotal, cqComplexityScoreTotal, complexityTierConfig)
	);

	return Number(specialModUpchargeAmount.plus(cqComplexityAmount).toFixed(2));
};

export const calculateQuoteTotal = (
	baseQuoteTotal: number,
	specialMods: models["SpecialModificationViewModel"][] | null,
	manualPercentAdjustment: string | null | undefined,
	manualFlatAdjustment?: string | null,
	cqComplexityScoreTotal?: number,
	complexityTierConfig?: models["CustomQuoteConfigViewModel"]
): number => {
	const basePrice = new Big(baseQuoteTotal ?? 0);
	const manualPercentAdjAmount = new Big(
		calculateManualPercentAdjustmentAmount(baseQuoteTotal, manualPercentAdjustment)
	);
	const manualFlatAdjAmount = new Big(sanitizeFlatAdjustment(manualFlatAdjustment));
	const customizationChargeAmount = new Big(
		calculateCustomizationChargeAmount(baseQuoteTotal, specialMods, cqComplexityScoreTotal, complexityTierConfig)
	);

	const total = basePrice.plus(manualPercentAdjAmount).plus(manualFlatAdjAmount).plus(customizationChargeAmount);
	const result = Number(total.toFixed(2));

	return result;
};
